import React, { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import Grid from "@mui/material/Grid";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import SchedulerEntryTimes from "./SchedulerEntryTimes";
import ScheduleEntryMission from "./ScheduleEntryMission";
import MissionFormContent from "../../Missions/MissionFormContent";
import Weekday from "../../../../utils/weekday";
import TimeSpan from "../../../../utils/time-span";
import ScheduleEntry, {
  getEntryScheduleTimeFromScheduledTimes,
  getNewScheduleEntry,
  ScheduleEntryTimes,
} from "../../../../interfaces/robot/scheduling/schedule-entry";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import Mission, {
  getDefaultMissionName,
  isDefaultMissionNameStructure,
  getNextColorForMission,
} from "../../../../interfaces/robot/missions/mission";
import { selectMissionsRestSet, selectRoomMissions } from "../../../../store/room/room-selectors";
import { selectIsReadOnly } from "../../../../store/app-configurations/app-configurations-selectors";
import { NavigatorName } from "../../../../enums/command-hub/navigator";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import { DeleteIcon, SaveIcon } from "../../../../utils/icons/icons";
import DialogWrapperWithClose from "../../../General/DialogWrapperWithClose";
import { selectScheduleEntries } from "../../../../store/room/scheduler/scheduler-selectors";
import { validateName } from "../../../../validation/mission-validation";

const useStyles = makeStyles()((theme) => ({
  scrollbar: {
    "&::-webkit-scrollbar": {
      width: theme.spacing(1.8),
    },
    "&::-webkit-scrollbar-track": {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[100],
    },
    "&::-webkit-scrollbar-thumb": {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[300],
    },
  },
  topShadow: {
    position: "sticky",
    top: 0,
    height: 16,
    pointerEvents: "none",
    background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.2), transparent)",
    zIndex: 2,
  },

  bottomShadow: {
    position: "sticky",
    bottom: 0,
    height: 16,
    pointerEvents: "none",
    background: "linear-gradient(to top, rgba(0, 0, 0, 0.2), transparent)",
    zIndex: 2,
  },
}));

const ScheduleEntryForm: React.FC<{
  roomId: string;
  linkedMissionId?: string;
  scheduleEntry?: ScheduleEntry;
  isTemporary?: boolean;
  onDialogClose: (closingOnSave?: boolean) => void;
}> = ({ roomId, linkedMissionId, scheduleEntry, isTemporary = false, onDialogClose }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const dispatch = useSharedDispatch();

  const { postNewScheduleEntry, deleteScheduleEntryById, patchScheduleEntry, patchMission, postNewMission } =
    useSharedActions();

  const isReadOnly = useSharedSelector((state) => selectIsReadOnly(state));
  const roomMissions = useSharedSelector((state) => selectRoomMissions(state, roomId));
  const missionsRestSet = useSharedSelector((state) => selectMissionsRestSet(state));
  const robotSchedules = useSharedSelector((state) => selectScheduleEntries(state));
  const [scheduledTimes, setScheduledTimes] = useState<ScheduleEntryTimes[]>([
    {
      weekdayFrom: scheduleEntry ? scheduleEntry.weekdayFrom : Weekday.Monday,
      weekdayTo: scheduleEntry ? scheduleEntry.weekdayTo : Weekday.Monday,
      timeFrom: scheduleEntry ? scheduleEntry.timeFrom : ({ hours: 0, minutes: 0 } as TimeSpan),
      timeTo: scheduleEntry ? scheduleEntry.timeTo : ({ hours: 0, minutes: 30 } as TimeSpan),
    },
  ]);
  const [missionId, setMissionId] = useState(scheduleEntry ? scheduleEntry.missionId : (linkedMissionId ?? ""));
  const [scheduleEnabled, setScheduleEnabled] = useState(scheduleEntry ? scheduleEntry.enabled : true);
  const [isScheduleInvalid, setIsScheduleInvalid] = useState(true);
  const [isMissionSaved, setIsMissionSaved] = useState(false);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const existingMission = roomMissions.find((m) => m.id === missionId);

  const [formMission, setFormMission] = useState<Mission | null>(existingMission ?? null);
  const [isMissionConfigChanged, setIsMissionConfigChanged] = useState(false);

  const handleMissionChange = (updates: Partial<Mission>) => {
    setIsMissionConfigChanged(true);
    if (formMission && isDefaultMissionNameStructure(formMission.name) && updates.mode && updates.repeat) {
      updates.name = getDefaultMissionName(updates.mode, roomMissions, updates.repeat, existingMission?.id);
    }
    setFormMission((prev) => (prev ? { ...prev, ...updates } : ({ ...updates } as Mission)));
  };

  useEffect(() => {
    if (existingMission) {
      setFormMission(existingMission);
    } else {
      setFormMission({
        color: getNextColorForMission(roomMissions.length),
        repeat: 1,
        fixedOrder: false,
        doWallFollow: true,
        selectedLandmarkIds: [],
        selectedZoneIds: [],
        name: getDefaultMissionName(NavigatorName.ZoneCleaning, roomMissions, 1),
        room: roomId,
        id: missionId,
        mode: NavigatorName.ZoneCleaning,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId]);

  const closeDialogHandler = useCallback(
    (closingOnSave: boolean) => {
      setIsMissionSaved(false);
      if (scheduleEntry && !closingOnSave) {
        onDialogClose(false);
      } else {
        onDialogClose(closingOnSave);
      }
    },
    [scheduleEntry, onDialogClose],
  );

  useEffect(() => {
    if (
      isMissionSaved &&
      (missionsRestSet.patchStatus === HttpFetchStatus.Success ||
        missionsRestSet.postStatus === HttpFetchStatus.Success)
    ) {
      if (scheduleEntry && !isTemporary) {
        const scheduleEntrySchedule = getEntryScheduleTimeFromScheduledTimes(scheduleEntry, scheduledTimes);
        const otherSchedules = scheduledTimes.filter((s) => !scheduleEntrySchedule || s !== scheduleEntrySchedule);

        if (scheduleEntrySchedule) {
          // entry config updated
          dispatch(
            patchScheduleEntry({
              id: scheduleEntry.id,
              data: {
                timeFrom: scheduleEntrySchedule.timeFrom,
                timeTo: scheduleEntrySchedule.timeTo,
                weekdayFrom: scheduleEntrySchedule.weekdayFrom,
                weekdayTo: scheduleEntrySchedule.weekdayTo,
                enabled: scheduleEnabled,
                missionId: missionId,
              },
            }),
          );
          otherSchedules.forEach((s) => {
            dispatch(
              postNewScheduleEntry(
                getNewScheduleEntry(s.weekdayFrom, s.weekdayTo, s.timeFrom, s.timeTo, scheduleEnabled, missionId),
              ),
            );
          });
        } else if (!scheduleEntrySchedule) {
          if (otherSchedules.length === 1) {
            // entry time or day updated
            dispatch(
              patchScheduleEntry({
                id: scheduleEntry.id,
                data: {
                  timeFrom: otherSchedules[0].timeFrom,
                  timeTo: otherSchedules[0].timeTo,
                  weekdayFrom: otherSchedules[0].weekdayFrom,
                  weekdayTo: otherSchedules[0].weekdayTo,
                  enabled: scheduleEnabled,
                  missionId: missionId,
                },
              }),
            );
          } else if (otherSchedules.length > 0) {
            // entry changed in multiple days, create new schedules
            dispatch(deleteScheduleEntryById(scheduleEntry.id));
            otherSchedules.forEach((s) => {
              dispatch(
                postNewScheduleEntry(
                  getNewScheduleEntry(s.weekdayFrom, s.weekdayTo, s.timeFrom, s.timeTo, scheduleEnabled, missionId),
                ),
              );
            });
          }
        }
        closeDialogHandler(true);
      } else {
        scheduledTimes.forEach((s) => {
          dispatch(
            postNewScheduleEntry(
              getNewScheduleEntry(s.weekdayFrom, s.weekdayTo, s.timeFrom, s.timeTo, scheduleEnabled, missionId),
            ),
          );
        });
        closeDialogHandler(false);
      }
    }
  }, [
    missionsRestSet.patchStatus,
    missionsRestSet.postStatus,
    closeDialogHandler,
    deleteScheduleEntryById,
    dispatch,
    isMissionSaved,
    isTemporary,
    missionId,
    patchScheduleEntry,
    postNewScheduleEntry,
    scheduleEnabled,
    scheduleEntry,
    scheduledTimes,
  ]);

  const missionIdChangeHandler = (newId: string) => {
    if (newId === "new") {
      newId = uuidv4();
    }
    setMissionId(newId);
    setIsMissionConfigChanged(false);
  };

  const scheduledTimesChangeHandler = (scheduledTimes: ScheduleEntryTimes[], noDaysSelected: boolean) => {
    setIsScheduleInvalid(noDaysSelected);
    setScheduledTimes(scheduledTimes);
  };

  const scheduleEnabledChangeHandler = () => setScheduleEnabled((previousState) => !previousState);

  const buttonDeleteClickHandler = () => {
    if (scheduleEntry) {
      dispatch(deleteScheduleEntryById(scheduleEntry.id));
    }
  };

  const keyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Delete" && scheduleEntry) {
      dispatch(deleteScheduleEntryById(scheduleEntry.id));
    }
  };

  const doubleClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const saveClickHandler = (_event: React.MouseEvent) => {
    if (formMission) {
      if (existingMission?.id == null) {
        dispatch(postNewMission(formMission));
      } else {
        dispatch(
          patchMission({
            id: missionId,
            data: formMission,
          }),
        );
      }
      setIsMissionSaved(true);
    }
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;

    setShowTopShadow(scrollTop > 0);
    // 16 is the height of the shadow, I have to subtract it in order to make it disappear when I reach the bottom
    setShowBottomShadow(scrollTop + clientHeight < (scrollTop == 0 ? scrollHeight : scrollHeight - 16));
  };

  const isSaveButtonActive =
    missionId !== "" &&
    scheduledTimes.length > 0 &&
    !isScheduleInvalid &&
    formMission?.repeat &&
    validateName(formMission.name).every((vR) => vR.isValid) &&
    formMission.mode.length > 0 &&
    ((formMission.mode === NavigatorName.ZoneCleaning &&
      formMission.selectedZoneIds != null &&
      formMission.selectedZoneIds.length > 0) ||
      (formMission.mode === NavigatorName.Maintenance &&
        formMission.selectedLandmarkIds != null &&
        formMission.selectedLandmarkIds.length > 0) ||
      (formMission.mode !== NavigatorName.ZoneCleaning && formMission.mode !== NavigatorName.Maintenance));

  return (
    <DialogWrapperWithClose
      open={true}
      fullWidth
      maxWidth="md"
      onClose={() => closeDialogHandler(false)}
      {...(scheduleEntry && !isTemporary ? { onKeyDown: keyDownHandler, onDoubleClick: doubleClickHandler } : {})}
      sx={{ "& .MuiPaper-root": { maxHeight: "80%" } }}
      title={t("input-label-schedule-configuration")}
      TransitionProps={{
        onEntered: () => {
          handleScroll();
        },
      }}
    >
      {showTopShadow && <Box className={classes.topShadow} />}
      <DialogContent
        className={classes.scrollbar}
        sx={{ pb: 0, overflowY: "auto" }}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <form style={{ opacity: isReadOnly ? 0.75 : 1, pointerEvents: isReadOnly ? "none" : "initial" }}>
          <fieldset disabled={isReadOnly} style={{ border: "none", minWidth: 0, padding: 0 }}>
            <Grid container gap={6}>
              <Grid item xs={12} gap={2}>
                <SchedulerEntryTimes
                  timeFrom={
                    scheduleEntry
                      ? scheduleEntry.timeFrom
                      : scheduledTimes.length >= 1
                        ? scheduledTimes[0].timeFrom
                        : { hours: 0, minutes: 0 }
                  }
                  timeTo={
                    scheduleEntry
                      ? scheduleEntry.timeTo
                      : scheduledTimes.length >= 1
                        ? scheduledTimes[0].timeTo
                        : { hours: 0, minutes: 30 }
                  }
                  weekdaysFromTo={
                    scheduleEntry
                      ? {
                          weekdayFrom: scheduleEntry.weekdayFrom,
                          weekdayTo: scheduleEntry.weekdayTo,
                        }
                      : undefined
                  }
                  recurringWeekdays={
                    scheduleEntry
                      ? [scheduleEntry.weekdayFrom]
                      : scheduledTimes.length !== 1
                        ? scheduledTimes.map((s) => s.weekdayFrom)
                        : []
                  }
                  onScheduledTimesChange={scheduledTimesChangeHandler}
                />
                <FormControlLabel
                  sx={{ paddingTop: 2 }}
                  control={
                    <Switch
                      checked={scheduleEnabled}
                      onChange={scheduleEnabledChangeHandler}
                      name="checkedB"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      {scheduleEnabled ? t("scheduler-enabled") : t("scheduler-disabled")}
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="medium">
                  {t("input-label-mission")}
                </Typography>
                <ScheduleEntryMission
                  missionId={missionId}
                  onMissionIdChange={missionIdChangeHandler}
                  roomId={roomId}
                />
              </Grid>
              {formMission && (
                <Grid item xs={12}>
                  <Collapse in={missionId !== "" && formMission !== null}>
                    <Typography variant="h6" fontWeight="medium">
                      {t("input-label-mission-configuration")}
                    </Typography>
                    <MissionFormContent
                      formMission={formMission}
                      onMissionChange={handleMissionChange}
                      roomId={roomId}
                    />
                  </Collapse>
                </Grid>
              )}
            </Grid>
          </fieldset>
        </form>
        <Stack direction="row" justifyContent="flex-end" pt={3} pb={1} gap={2}>
          {scheduleEntry && !isTemporary && (
            <Button
              variant="text"
              color="secondary"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={buttonDeleteClickHandler}
              disabled={isReadOnly}
            >
              {t("button-delete")}
            </Button>
          )}
          <Tooltip title={isMissionConfigChanged && existingMission ? t("tooltip-mission-updated") : undefined}>
            <span>
              <Button
                variant="contained"
                color={isMissionConfigChanged && existingMission ? "warning" : "primary"}
                size="small"
                startIcon={<SaveIcon />}
                onClick={saveClickHandler}
                disabled={!isSaveButtonActive || isReadOnly}
              >
                {t("button-save")}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogContent>
      {showBottomShadow && <Box className={classes.bottomShadow} />}
    </DialogWrapperWithClose>
  );
};

export default ScheduleEntryForm;
